"use client";
import clsx from "clsx";
import { type MouseEvent, type PointerEvent, type ReactNode, useEffect, useRef, useState } from "react";

type Props = {
  className?: string;
  children: ReactNode;
};
export const ChildrenScroller = ({ className, children }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const dragRef = useRef({ isDown: false, startX: 0, scrollLeft: 0, hasMoved: false });
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const updateScrollState = () => {
      setCanScrollRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
    };

    updateScrollState();

    el.addEventListener("scroll", updateScrollState, { passive: true });
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      resizeObserver.disconnect();
    };
  }, []);

  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== "mouse") return;
    const el = ref.current;
    if (!el) return;
    dragRef.current = { isDown: true, startX: e.clientX, scrollLeft: el.scrollLeft, hasMoved: false };
  };

  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el || !dragRef.current.isDown) return;
    const dx = e.clientX - dragRef.current.startX;
    if (Math.abs(dx) > 3) {
      dragRef.current.hasMoved = true;
      el.scrollLeft = dragRef.current.scrollLeft - dx;
    }
  };

  const handlePointerEnd = () => {
    dragRef.current.isDown = false;
  };

  const handleClickCapture = (e: MouseEvent<HTMLDivElement>) => {
    if (dragRef.current.hasMoved) {
      e.preventDefault();
      e.stopPropagation();
      dragRef.current.hasMoved = false;
    }
  };

  return (
    <div
      ref={ref}
      className={clsx(className, "overscroll-x-contain", {
        "[mask-image:linear-gradient(90deg,black_75%,transparent_100%)]": canScrollRight,
      })}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerLeave={handlePointerEnd}
      onClickCapture={handleClickCapture}
      onDragStart={(e) => e.preventDefault()}
    >
      {children}
    </div>
  );
};
